import api from '@/api';

import { PageResult } from '@/core/request/PageResult';

import {
  WorkOrder,
  CreateWorkOrderRequest,
  UpdateWorkOrderRequest,
  ListWorkOrdersResult,
  ListWorkOrdersQuery,
  WorkOrderCalendarQueryParams,
  WorkorderItemCalendar,
  ComplementReplicateRequest,
  ComplementReplicateResponse,
  WorkOrderCreateByPorto,
  WorkOrderMigrateConsultListRequest,
  WorkOrderMigrateConsultListResponse,
  WorkOrderMigrateConsultItemResponse,
  WorkOrderCustomerAddressRequest,
  WorkOrderPartnerDiscountRequest,
  WorkOrderComplementBenefitList,
  WorkOrderEmployeerDiscountRequest,
  WorkOrderComplementBenefitListRequest,
  WorkOrderAddPolicyRequest,
  WorkOrderSystemOriginListResponse,
  WorkOrderMigrationConsultBindComplement,
} from './WorkOrders.types';

import { API_BASE_URL } from '..';

const CONTROLLER_URL = `${API_BASE_URL}/workorders`;

export async function listWorkOrders(params?: ListWorkOrdersQuery) {
  const result = (await api.get(`${CONTROLLER_URL}`, {
    ...params,
    companiesIds: params?.companiesIds?.toString(),
    status: params?.status?.toString(),
    type: params?.type?.toString(),
    tagsId: params?.tagsId?.toString(),
    complementOrigin: params?.complementOrigin?.toString(),
  })) as unknown;

  return result as PageResult<ListWorkOrdersResult>;
}

export async function getWorkOrder(id?: number): Promise<WorkOrder> {
  const result = (await api.get(`${CONTROLLER_URL}/${id}`, null)) as unknown;

  return result as WorkOrder;
}

export async function createWorkOrder(
  obj: CreateWorkOrderRequest,
): Promise<WorkOrder> {
  const result = (await api.post(`${CONTROLLER_URL}`, obj, null)) as unknown;

  return result as WorkOrder;
}

export async function updateWorkOrder(
  obj: UpdateWorkOrderRequest,
  workOrderId?: number,
): Promise<WorkOrder> {
  const result = (await api.put(
    `${CONTROLLER_URL}/${workOrderId}`,
    obj,
    null,
  )) as unknown;

  return result as WorkOrder;
}

export async function getWorkOrderCalendar(
  params: WorkOrderCalendarQueryParams,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/calendar`,
    params,
  )) as unknown;

  return result as WorkorderItemCalendar[];
}

export async function replicateWorkOrder(
  obj: ComplementReplicateRequest,
): Promise<ComplementReplicateResponse> {
  const result = (await api.post(
    `${CONTROLLER_URL}/replicate`,
    obj,
    null,
  )) as unknown;

  return result as ComplementReplicateResponse;
}

export async function createWorkOrderByPorto(data: WorkOrderCreateByPorto) {
  const result = (await api.post(
    `${CONTROLLER_URL}/by-porto`,
    data,
  )) as unknown;
  return result as { workOrderComplementId: number; workOrderId: number };
}

export async function workOrdersMigrateConsult(
  params?: WorkOrderMigrateConsultListRequest,
) {
  const result = (await api.get(
    `${CONTROLLER_URL}/consult`,
    params,
  )) as unknown;
  return result as WorkOrderMigrateConsultListResponse;
}

export async function workOrdersMigrateConsultItens(consultId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/consult/${consultId}/items`,
    null,
  )) as unknown;
  return result as WorkOrderMigrateConsultItemResponse;
}

export async function workOrdersMigrateConsultBindComplement(
  data: WorkOrderMigrationConsultBindComplement,
) {
  const result = (await api.patch(
    `${CONTROLLER_URL}/consult/bind-with-complement`,
    data,
    null,
  )) as unknown;
  return result as void;
}

export async function workOrderCustomerAddressUpdate(
  params: WorkOrderCustomerAddressRequest,
) {
  const { workOrderId, ...rest } = params;
  const result = (await api.patch(
    `${CONTROLLER_URL}/${workOrderId}/customer-address`,
    rest,
    null,
  )) as unknown;

  return result;
}

export async function workOrderPartnerDiscount({
  workOrderId,
  ...data
}: WorkOrderPartnerDiscountRequest) {
  const result = (await api.patch(
    `${CONTROLLER_URL}/${workOrderId}/partner-discount`,
    data,
    null,
  )) as unknown;
  return result as void;
}

export async function workOrderComplementBenefitList({
  workOrderId,
  ...params
}: WorkOrderComplementBenefitListRequest) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${workOrderId}/benefits`,
    params,
  )) as unknown;
  return result as WorkOrderComplementBenefitList[];
}

export async function workOrderEmployeerDiscount({
  workOrderId,
  ...data
}: WorkOrderEmployeerDiscountRequest) {
  const result = (await api.patch(
    `${CONTROLLER_URL}/${workOrderId}/employee-discount`,
    data,
    null,
  )) as unknown;
  return result as void;
}

export async function workOrderAddPolicy(data: WorkOrderAddPolicyRequest) {
  const result = (await api.put(
    `${CONTROLLER_URL}/add-policy`,
    data,
    null,
  )) as unknown;
  return result as void;
}

export async function workOrderSystemOriginList() {
  const result = (await api.get(
    `${CONTROLLER_URL}/system-origins`,
    null,
  )) as unknown;

  return result as WorkOrderSystemOriginListResponse;
}
